import { alpha, type Theme } from "@mui/material/styles";
import type { MRT_Row, MRT_RowData, MRT_TableOptions } from "material-react-table";
import { SEEDIT_FONT_FAMILY_MONO } from "./seedit-theme.tokens";

export const SEEDIT_MRT_INITIAL_STATE = {
  density: "comfortable" as const,
  showColumnFilters: true
};

const subtleDivider = (theme: Theme) =>
  alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.12 : 0.08);

type RowPropsParams<TData extends MRT_RowData> = {
  row: MRT_Row<TData>;
};

export function getSeeditMrtRowSx<TData extends MRT_RowData>(
  theme: Theme,
  { row }: RowPropsParams<TData>
) {
  const isDark = theme.palette.mode === "dark";
  const isSelected = row.getIsSelected();

  const selectedBg = alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08);

  return {
    backgroundColor: isSelected ? selectedBg : "inherit",
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.short
    }),
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none"
    },
    "&:hover": {
      backgroundColor: isSelected
        ? alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)
        : theme.palette.action.hover
    },
    "& td": {
      borderBottom: `1px solid ${subtleDivider(theme)}`
    },
    "&:last-child td": {
      borderBottom: "none"
    }
  };
}

export function createSeeditMrtBodyRowProps<TData extends MRT_RowData>(theme: Theme) {
  return ({ row }: RowPropsParams<TData>) => ({
    sx: getSeeditMrtRowSx(theme, { row })
  });
}

/** Shared Material React Table styling aligned with the Seedit design system. */
export function getSeeditMrtTableOptions<TData extends MRT_RowData>(
  theme: Theme
): Partial<MRT_TableOptions<TData>> {
  const paper = theme.palette.background.paper;
  const divider = theme.palette.divider;
  const isDark = theme.palette.mode === "dark";
  const headerBg = isDark
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.text.primary, 0.04);

  return {
    layoutMode: "semantic",
    columnFilterDisplayMode: "subheader",
    defaultColumn: {
      minSize: 120,
      size: 180
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        size: 56,
        minSize: 56,
        grow: false
      },
      "mrt-row-actions": {
        size: 96,
        minSize: 96,
        grow: false
      }
    },
    enableColumnActions: true,
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
      sx: { minWidth: 200 }
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 30],
      variant: "outlined",
      shape: "rounded"
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        backgroundColor: paper,
        border: `1px solid ${divider}`,
        borderRadius: 1.5,
        boxShadow: "none",
        width: "inherit",
        overflow: "hidden"
      }
    },
    muiTableContainerProps: {
      sx: {
        backgroundColor: paper
      }
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: headerBg,
        fontWeight: 600,
        fontSize: "0.8125rem",
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${subtleDivider(theme)}`,
        px: 2,
        py: 1.25,
        verticalAlign: "top",
        "& .Mui-TableHeadCell-Content": {
          gap: 0.5,
          minWidth: 0,
          width: "100%"
        },
        "& .Mui-TableHeadCell-Content-Labels": {
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        "& .Mui-TableHeadCell-Content-Actions": {
          flexShrink: 0
        },
        "& .MuiCollapse-root": {
          width: "100%"
        }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.875rem",
        borderBottom: "none",
        px: 2,
        py: 1.25,
        fontVariantNumeric: "tabular-nums"
      }
    },
    muiTableBodyRowProps: createSeeditMrtBodyRowProps<TData>(theme),
    muiTopToolbarProps: {
      sx: {
        backgroundColor: paper,
        borderBottom: `1px solid ${divider}`,
        gap: 1,
        px: 1.5,
        py: 0.75,
        minHeight: 52
      }
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: paper,
        borderTop: `1px solid ${divider}`,
        px: 1.5,
        py: 0.5
      }
    },
    muiFilterTextFieldProps: {
      size: "small",
      variant: "standard",
      sx: {
        mt: 0.75,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        "& .MuiInput-underline:before": {
          borderBottomColor: subtleDivider(theme)
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottomColor: alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 0.28 : 0.2
          )
        },
        "& .MuiInput-underline:after": {
          borderBottomWidth: 1
        }
      }
    },
    muiTableProps: {
      sx: {
        "& .MuiTableCell-root[data-index='numeric']": {
          fontFamily: SEEDIT_FONT_FAMILY_MONO
        }
      }
    }
  };
}

export function withSeeditMrtInitialState<T extends Record<string, unknown>>(initialState?: T) {
  return {
    ...SEEDIT_MRT_INITIAL_STATE,
    ...initialState
  };
}
